import { Button, buttonClasses, useBrand } from "@fprint/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SignInButton, SignOutButton, useAuth } from "../lib/auth";
import { usePublicConfig } from "../lib/publicConfig";
import { homeSignInFrom } from "../lib/resumeQuote";

interface Health {
  status: "ok" | "degraded" | "down";
  lab_reachable?: boolean | null;
}

export function Home() {
  const brand = useBrand();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const [health, setHealth] = useState<Health | null>(null);
  const publicConfig = usePublicConfig();

  useEffect(() => {
    fetch("/api/health")
      .then((r) => (r.ok ? r.json() : null))
      .then(setHealth)
      .catch(() => setHealth({ status: "down" }));
  }, []);

  const serviceDown = health?.status === "down" || health?.lab_reachable === false;

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center gap-8 px-6 py-16 text-center">
      <h1 className="text-5xl font-bold tracking-tight" data-testid="brand-name">
        {brand.name}
      </h1>
      <p className="text-xl text-muted-fg">{brand.copy["hero.title"]}</p>
      <p className="text-base text-muted-fg">{brand.copy["hero.subtitle"]}</p>

      {serviceDown && (
        <div
          role="alert"
          className="rounded-md border border-warning bg-warning/10 px-4 py-3 text-sm text-warning-fg"
          data-testid="service-down-banner"
        >
          {brand.copy["status.serviceDown"]}
        </div>
      )}

      <div
        className="flex w-full max-w-sm flex-col gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center"
        data-testid="home-cta-row"
      >
        {/* No Link wrapper: a <Button disabled> nested in a <Link> is invalid
            HTML and clicks/Enter fall through to the anchor even when the
            service is down. A real button + navigate-on-click means the
            disabled state genuinely blocks navigation. */}
        <Button
          size="lg"
          className="w-full sm:w-auto"
          disabled={serviceDown}
          onClick={() => navigate("/quote")}
        >
          {brand.copy["cta.upload"]}
        </Button>
        {isSignedIn ? (
          <>
            <Link
              to="/orders"
              className={buttonClasses({
                size: "lg",
                variant: "secondary",
                className: "w-full sm:w-auto",
              })}
            >
              My orders
            </Link>
            <SignOutButton size="lg" className="w-full sm:w-auto" />
          </>
        ) : (
          <SignInButton from={homeSignInFrom()} size="lg" className="w-full sm:w-auto" />
        )}
      </div>

      {publicConfig?.custom_work_enabled && (
        <p className="text-sm text-muted-fg" data-testid="custom-work-entry">
          Need design help, painting or something out of the ordinary?{" "}
          <Link to="/custom-work" className="underline">
            Request custom work
          </Link>
          .
        </p>
      )}
    </main>
  );
}
